
import { AbsDropDown, Button, COLORS, Input, Label, Modal } from "abs-ui-components";
import { CORRECTION_FIELDS, FUEL_FIELDS, REPORT_FIELDS } from "./FieldConfigs";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import FieldInput from "./FieldInput";
import { emptyCorrectionRow, emptyFuelRow } from "./CIIVerifiedInputHelpers";
import DynamicSection from "./DynamicSection";

const ReportingPeriod = ({
    period,
    index,
    totalPeriods,
    onRemovePeriod,
    onChangeReport,
    onChangeRow,
    onAddRow,
    onRemoveRow,
    disabled,
    handleConfirmCII,
    saving,
    errors,
    expandedPanel,
    onAccordionChange,
}) => {
    const updateReport = (name, val) => onChangeReport(period.id, name, val);

    const addFuel = () => onAddRow(period.id, "fuelConsumptions", emptyFuelRow);
    const addCorrection = () =>
        onAddRow(period.id, "correctionFactors", emptyCorrectionRow);

    const removeFuel = (rowIndex) =>
        onRemoveRow(period.id, "fuelConsumptions", rowIndex);
    const removeCorrection = (rowIndex) =>
        onRemoveRow(period.id, "correctionFactors", rowIndex);

    const updateFuelRow = (rowIndex, name, val) =>
        onChangeRow(period.id, "fuelConsumptions", rowIndex, name, val);
    const updateCorrectionRow = (rowIndex, name, val) =>
        onChangeRow(period.id, "correctionFactors", rowIndex, name, val);

    return (
        <Accordion
            expanded={expandedPanel === period.id}
            onChange={onAccordionChange(period.id)}
            style={{
                marginBottom: 6,
                marginTop: 6,
                border: "1px solid #d7dee8",
                borderRadius: 8,
                boxShadow: "none",
                transition: { timeout: 0 },
            }}
            sx={{
                "&:before": { display: "none" },
                "&.Mui-expanded": {
                    margin: "0 !important",
                },
            }}
        >
            <AccordionSummary
                aria-controls={`panel-${period.id}-content`}
                id={`panel-${period.id}-header`}
                style={{
                    background: "#002A4E",
                }}
                sx={{
                    "&.Mui-expanded": {
                        minHeight: 48,
                    },
                    "& .MuiAccordionSummary-content": {
                        margin: "0",
                    },
                    "& .MuiAccordionSummary-content.Mui-expanded": {
                        margin: "0",
                    },
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                    }}
                >
                    <span
                        style={{
                            display: "inline-block",
                            letterSpacing: 0.5,
                            color: "#ffffff",
                            fontSize: "20px",
                            fontWeight: "bold"
                        }}

                    >
                        {expandedPanel === period.id ? "−" : "+"}
                    </span>
                    <span
                        style={{
                            display: "inline-block",
                            fontSize: 12,
                            fontWeight: 600,
                            letterSpacing: 0.5,
                            color: "#ffffff",
                        }}
                    >
                        {period.periodLabel}
                    </span>

                    {period.confirmedAt && period.dirty && (
                        <span style={{ color: "red", fontSize: 12 }}>
                            Changes are unsaved
                        </span>
                    )}
                </div>
            </AccordionSummary>

            <AccordionDetails style={{ padding: 16 }}>
                {/* Report fields */}
                <div
                    className="mb-4"
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 1fr)",
                        gap: 16,
                    }}
                >
                    {REPORT_FIELDS.map((f) => (
                        <div key={f.name}>
                            <Label name={f.label} bold />

                            {f.type === "date" ? (
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        format="DD/MMM/YYYY"
                                        value={
                                            period[f.name]
                                                ? dayjs(period[f.name])
                                                : null
                                        }
                                        onChange={(val) =>
                                            updateReport(
                                                f.name,
                                                val && val.isValid()
                                                    ? val.format("YYYY-MM-DD")
                                                    : ""
                                            )
                                        }
                                        disabled={disabled}
                                        slotProps={{
                                            textField: {
                                                size: "small",
                                                fullWidth: true,
                                                placeholder: "DD/MMM/YYYY",
                                                sx: {
                                                    "& .MuiInputBase-root": {
                                                        minHeight: "40px !important",
                                                        fontSize: 13,
                                                    },
                                                    "& .MuiInputBase-input": {
                                                        padding: "4px 8px",
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            ) : (
                                <FieldInput
                                    field={f}
                                    row={period}
                                    value={period[f.name]}
                                    error={errors?.[f.name]}
                                    onChange={updateReport}
                                    disabled={disabled}
                                />
                            )}

                            {f.type === "date" && errors?.[f.name] && (
                                <div className="error-msg">
                                    {errors[f.name]}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Fuel Consumed */}
                <DynamicSection
                    title="Fuel Consumed"
                    addLabel="+ Add New Fuel"
                    fields={FUEL_FIELDS}
                    rows={period.fuelConsumptions}
                    errors={errors}
                    errorPrefix="fuelConsumptions"
                    onAddRow={addFuel}
                    onRemoveRow={removeFuel}
                    onChangeCell={updateFuelRow}
                    disabled={disabled}
                />

                {/* CII Corrections */}
                <DynamicSection
                    title="CII Correction Factors and Voyage Adjustments"
                    addLabel="+ Add New CII Correction"
                    fields={CORRECTION_FIELDS}
                    rows={period.correctionFactors}
                    errors={errors}
                    errorPrefix="correctionFactors"
                    onAddRow={addCorrection}
                    onRemoveRow={removeCorrection}
                    onChangeCell={updateCorrectionRow}
                    disabled={disabled}
                />

                {/* Action buttons */}
                <div className="d-flex justify-content-end mt-4">
                    <Button
                        className="mr-2"
                        disabled={
                            disabled ||
                            saving ||
                            (period.confirmedAt && !period.dirty)
                        }
                        onClick={() => handleConfirmCII(period.id)}
                    >
                        {saving ? "Saving…" : "Confirm Verified CII Data"}
                    </Button>

                    {totalPeriods > 0 && (
                        <Button
                            primaryColor={COLORS.RED.DARK}
                            disabled={disabled || saving}
                            onClick={() => onRemovePeriod(period.id)}
                        >
                             Remove Verified CII Data
                        </Button>
                    )}
                </div>
            </AccordionDetails>
        </Accordion>
    );
};

export default ReportingPeriod;